import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropDownContainer, ToggleIconButton } from '../TradeNameWrapper.style';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { DDLabel, DDWrapper, DropDownOptionsList, RotationContainer, RotationItem, SelectedListItem, ClearButton } from './RotationDropDown.style';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { getVoyageNumberString } from 'src/utils/stowage/stowageRotationUtils';

interface RotationSelectionDropDownProps {
    selectedItem?: string;
    upcomingPortCalls?: RotationPortCall[];
    onCallChange: (portCall: RotationPortCall, berthCall: RotationBerthCall | null) => void;
    onClearSelection?: () => void;
    tabIndex?: number;
    label?: string;
}

const RotationDropDown: React.FC<RotationSelectionDropDownProps> = ({
    selectedItem = 'Select port call',
    upcomingPortCalls = [],
    onCallChange,
    onClearSelection,
    tabIndex,
    label = 'Unloading call',
}) => {
    const [isRotationSelectionListOpen, setIsRotationSelectionListOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const onOpenRotationSelectionList = (e: React.MouseEvent): void => {
        e.stopPropagation();
        setIsRotationSelectionListOpen(!isRotationSelectionListOpen);
    };

    const onSelectedCall = (event: React.MouseEvent<HTMLDivElement>, portCall: RotationPortCall, berthCall: RotationBerthCall | null): void => {
        event.stopPropagation();
        onCallChange(portCall, berthCall);
        setIsRotationSelectionListOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsRotationSelectionListOpen(false);
            }
        };

        if (isRotationSelectionListOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isRotationSelectionListOpen]);

    return (
        <DDWrapper className="field-wrapper">
            <DDLabel>{label}</DDLabel>
            <RotationContainer ref={dropdownRef}>
                <SelectedListItem onClick={onOpenRotationSelectionList} role="button" aria-expanded={isRotationSelectionListOpen}>
                    <span>{selectedItem}</span>
                </SelectedListItem>

                {upcomingPortCalls.length > 0 && isRotationSelectionListOpen && (
                    <DropDownOptionsList role="listbox">
                        <DropDownContainer>
                            {upcomingPortCalls.map((portCall) => (
                                <ul key={portCall.portCallGuid}>
                                    <li>
                                        <RotationItem
                                            onClick={(e) => onSelectedCall(e, portCall, null)}
                                            className="port-item selectable"
                                            role="option"
                                            aria-selected={selectedItem === portCall.portDisplayName}
                                        >
                                            <span>{getVoyageNumberString(portCall)} - {portCall.portDisplayName}</span>
                                        </RotationItem>
                                        {portCall.berthCalls?.length > 0 && (
                                            <ul>
                                                {portCall.berthCalls.map((berthCall) => (
                                                    <li key={berthCall.berthCallGuid}>
                                                        {berthCall?.berthName && (
                                                            <RotationItem
                                                                onClick={(e) => onSelectedCall(e, portCall, berthCall)}
                                                                className="berth-item selectable"
                                                                role="option"
                                                                aria-selected={selectedItem === berthCall.berthName}
                                                            >
                                                                <span> - {berthCall.berthName}</span>
                                                            </RotationItem>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                </ul>
                            ))}
                        </DropDownContainer>
                    </DropDownOptionsList>
                )}
                {onClearSelection && selectedItem && selectedItem.trim() !== '' && selectedItem !== 'Select port call' && (
                    <ClearButton
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClearSelection();
                        }}
                        title="Clear selection"
                    >
                        <FontAwesomeIcon icon="times" />
                    </ClearButton>
                )}
                <ToggleIconButton
                    onClick={(e) => onOpenRotationSelectionList(e)}
                    role="button"
                    aria-label={isRotationSelectionListOpen ? 'Close dropdown' : 'Open dropdown'}
                    tabIndex={tabIndex}
                >
                    <FontAwesomeIcon icon={isRotationSelectionListOpen ? 'chevron-up' : 'chevron-down'} />
                </ToggleIconButton>
            </RotationContainer>
        </DDWrapper>
    );
};

export default RotationDropDown;
